import * as THREE from 'three';

export const DEFAULT_MESH_COLOR = new THREE.Color(0xffffff);
export const DEFAULT_TOP_MESH_COLOR = new THREE.Color(0x75d1ff);
export const DEFAULT_BOTTOM_MESH_COLOR = new THREE.Color(0x6e82a0);
export const DEFAULT_LIFTEDMESH_COLOR = new THREE.Color(0xd1fdce);
export const DEFAULT_FOOTSHOE_COLOR = new THREE.Color(0xA5A5A5);
export const DEFAULT_LINE_COLOR = new THREE.Color(0.0, 0.0, 0.0);
export const DEFAULT_LINE_WIDTH = 1;
export const DEFAULT_CILICKED_ADDITION_COLOR = 0xff3333;
export const DEFAULT_ADDITON_MESH_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xff6600, //DEFAULT_MESH_COLOR.getHex(),
  metalness: 0.3,
  roughness: 0.5,
  side: THREE.DoubleSide,
  flatShading: false,  // Enabled flat shading to show more geometric detail
  shadowSide: THREE.FrontSide,
  opacity: 0.5,
  transparent: true,
  depthWrite: false,
  depthTest: true
});

export const DEFAULT_MESH_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xffffff, 
  vertexColors: true, 
  metalness: 0.6,
  roughness: 0.6,
  side: THREE.DoubleSide,
  flatShading: false,
  shadowSide: THREE.FrontSide, 
  opacity: 1.0,
  transparent: true,
});

export enum AdditionImode {
  Bottom = 'Bottom',
  Top = "Top"
}

export enum Hardness {
  ExtraHard = 'Extra Hard',
  Hard = 'Hard',
  Medium = 'Medium',
  Soft = 'Soft',
  ExtraSoft = 'Extra Soft',
}
export const HardnessValueMap: Record<Hardness, number> = {
  [Hardness.ExtraHard]: 0,
  [Hardness.Hard]: 1,
  [Hardness.Medium]: 2,
  [Hardness.Soft]: 3,
  [Hardness.ExtraSoft]: 4,
};

export const FrontendHardnessToBackend = (
  value: number | Hardness
): string | number | null => {
  if (value === null || value === undefined) return null;

  // If already a backend numeric enum
  if (typeof value === "number") {
    return value; // 0–4
  }

  // Frontend string enum → backend enum string
  const map: Record<Hardness, string> = {
    [Hardness.ExtraHard]: "ExtraHard",
    [Hardness.Hard]: "Hard",
    [Hardness.Medium]: "Medium",
    [Hardness.Soft]: "Soft",
    [Hardness.ExtraSoft]: "ExtraSoft",
  };

  return map[value] ?? null;
};

export const BackendHardnessToFrontend = (value: number | string): Hardness | null => {
  // If backend sends numeric enum
  if (typeof value === "number") {
    return [
      Hardness.ExtraHard,
      Hardness.Hard,
      Hardness.Medium,
      Hardness.Soft,
      Hardness.ExtraSoft,
    ][value] ?? null;
  }
  // If backend sends string enum
  if (typeof value === "string") {
    const map: Record<string, Hardness> = {
      ExtraHard: Hardness.ExtraHard,
      Hard: Hardness.Hard,
      Medium: Hardness.Medium,
      Soft: Hardness.Soft,
      ExtraSoft: Hardness.ExtraSoft,
    };

    return map[value] ?? null;
  }

  return null;
};


// Stable render ordering for additions by groupName
export const ADDITION_GROUP_RENDER_ORDER: Record<string, number> = {
  'Heel': 10,
  'Wedges': 20,
  'Met Bars': 30,
  'Met Domes': 40,
  'Metatarsal Pads': 50,
  'Plantar Metatarsal Pads': 60,
  'Toe Pads': 70,
  'Lateral Pronate Pads': 80,
  'Dancers Pad': 90,
  'Reliefs': 100,
  'Others': 110,
};

export function getAdditionRenderOrder(groupName: string | undefined): number {
  if (!groupName) return 1000;
  return ADDITION_GROUP_RENDER_ORDER[groupName] ?? 1000;
}

export const LOCAL_STORAGE_KEYS = {
  DEFAULT_PRINTERS: 'defaultprinters',
  ACCESS_TOKEN: 'accessToken',
  USER: 'user',
  LAST_OPENED_DESIGN: 'lastOpenedDesign',
  SAVE_AS_NEW_FOR_ADMIN: 'saveasnewforadmin',
  PRINT_HELPERS: 'printHelpers',
  PRINT_COUNTS: 'unsavedPrintCounts'
}

export const BONE_MESH_NAME_PREFIX = 'bones_mesh_group_'
export const BONE_MESH_NAME_RIGHT = BONE_MESH_NAME_PREFIX + '0'
export const BONE_MESH_NAME_LEFT = BONE_MESH_NAME_PREFIX + '1'

export const BONE_MARKER_NAME_PREFIX = 'bones_marker_group_'
export const BONE_MARKER_NAME_RIGHT = BONE_MARKER_NAME_PREFIX + '0'
export const BONE_MARKER_NAME_LEFT = BONE_MARKER_NAME_PREFIX + '1'


export const BONES_ALL_NAME_PREFIX = 'bones_all_group_'
export const BONES_ALL_NAME_RIGHT = BONES_ALL_NAME_PREFIX + '0'
export const BONES_ALL_NAME_LEFT = BONES_ALL_NAME_PREFIX + '1'

export const SHOEMARKERS_INSIDE_NAME_PREFIX = 'ShoeInsideMarker_'
export const SHOEMARKERS_OUTSIDE_NAME_PREFIX = 'ShoeOutsideMarker_'

export enum MeasurementSystem {
  metric = 'metric',
  imperial = 'imperial',
}